import React, { useState } from "react";
import PageShell from "../dashboard/PageShell";
import ProjectFormActions from "../../components/forms/ProjectForm/ProjectFormActions";

interface AddNewIntellectualPropertyProps {}

interface IntellectualPropertyFormData {
  productName: string;
  certificateNumber: string;
  certificateType: string;
  ownerName: string;
  managingUnit: string;

  issueDay: string;
  issueMonth: string;
  issueYear: string; // (*)

  publicationDay: string;
  publicationMonth: string;
  publicationYear: string; // (*)

  applicationDay: string;
  applicationMonth: string;
  applicationYear: string; // (*)

  expirationDay: string;
  expirationMonth: string;
  expirationYear: string; // (*)

  evidence: string; // Minh chứng kèm
  notes: string; // Ghi chú
  attachedFiles: File[];
}

const days = Array.from({ length: 31 }, (_, i) => `${i + 1}`);
const months = Array.from({ length: 12 }, (_, i) => `${i + 1}`);
const years = Array.from({ length: 51 }, (_, i) => `${2000 + i}`);

const AddNewIntellectualProperty: React.FC<
  AddNewIntellectualPropertyProps
> = () => {
  const [formData, setFormData] = useState<IntellectualPropertyFormData>({
    productName: "",
    certificateNumber: "",
    certificateType: "",
    ownerName: "",
    managingUnit: "",
    issueDay: "",
    issueMonth: "",
    issueYear: "",
    publicationDay: "",
    publicationMonth: "",
    publicationYear: "",
    applicationDay: "",
    applicationMonth: "",
    applicationYear: "",
    expirationDay: "",
    expirationMonth: "",
    expirationYear: "",
    evidence: "",
    notes: "",
    attachedFiles: [],
  });

  const handleInputChange = (
    field: keyof IntellectualPropertyFormData,
    value: string | File[]
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value as never,
    }));
  };

  const handleFileSelect = (files: FileList | null) => {
    if (files) {
      const newFiles = Array.from(files);
      handleInputChange("attachedFiles", [
        ...formData.attachedFiles,
        ...newFiles,
      ]);
    }
  };

  const handleSave = () => {
    console.log("Saving IP product:", formData);
  };

  return (
    <PageShell title="Thêm mới Sản phẩm sở hữu trí tuệ">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-800">
            Thêm mới Sản phẩm sở hữu trí tuệ
          </h1>
        </div>

        {/* Form Container */}
        <div className="bg-white rounded-lg p-4">
          <div className="space-y-6">
            {/* Row 1: Tên sản phẩm sở hữu trí tuệ */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tên sản phẩm sở hữu trí tuệ{" "}
                <span className="text-red-500">(*)</span>
              </label>
              <input
                type="text"
                value={formData.productName}
                onChange={(e) =>
                  handleInputChange("productName", e.target.value)
                }
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Nhập tên sản phẩm..."
              />
            </div>

            {/* Row 2: Số bằng - Loại văn bằng sở hữu */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Số bằng
                </label>
                <input
                  type="text"
                  value={formData.certificateNumber}
                  onChange={(e) =>
                    handleInputChange("certificateNumber", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nhập số bằng..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Loại văn bằng sở hữu <span className="text-red-500">(*)</span>
                </label>
                <select
                  value={formData.certificateType}
                  onChange={(e) =>
                    handleInputChange("certificateType", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option>- Chọn loại -</option>
                  <option>Bằng sáng chế</option>
                  <option>Giải pháp hữu ích</option>
                  <option>Kiểu dáng công nghiệp</option>
                  <option>Nhãn hiệu</option>
                </select>
              </div>
            </div>

            {/* Row 3: Tên chủ sở hữu - Đơn vị/ phòng ban quản lý */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tên chủ sở hữu
                </label>
                <select
                  value={formData.ownerName}
                  onChange={(e) =>
                    handleInputChange("ownerName", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option>- Chọn tên chủ sở hữu -</option>
                  <option>Nguyễn Văn A</option>
                  <option>Trần Thị B</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Đơn vị/ phòng ban quản lý{" "}
                  <span className="text-red-500">(*)</span>
                </label>
                <select
                  value={formData.managingUnit}
                  onChange={(e) =>
                    handleInputChange("managingUnit", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option>- Chọn đơn vị/ phòng ban -</option>
                  <option>Khoa CNTT</option>
                  <option>Phòng Khoa học</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Application Date */}
              <div className=" rounded-md p-4 bg-gray-50">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Năm nộp đơn <span className="text-red-500">(*)</span>
                </label>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <select
                    value={formData.applicationDay}
                    onChange={(e) =>
                      handleInputChange("applicationDay", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option>- Chọn ngày -</option>
                    {days.map((d) => (
                      <option key={`ad-${d}`}>{d}</option>
                    ))}
                  </select>
                  <select
                    value={formData.applicationMonth}
                    onChange={(e) =>
                      handleInputChange("applicationMonth", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option>- Chọn tháng -</option>
                    {months.map((m) => (
                      <option key={`am-${m}`}>{m}</option>
                    ))}
                  </select>
                  <select
                    value={formData.applicationYear}
                    onChange={(e) =>
                      handleInputChange("applicationYear", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option>- Chọn năm -</option>
                    {years.map((y) => (
                      <option key={`ay-${y}`}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>
              {/* Issue Date */}
              <div className=" rounded-md p-4 bg-gray-50">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Năm cấp bằng <span className="text-red-500">(*)</span>
                </label>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <select
                    value={formData.issueDay}
                    onChange={(e) =>
                      handleInputChange("issueDay", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option>- Chọn ngày -</option>
                    {days.map((d) => (
                      <option key={`d-${d}`}>{d}</option>
                    ))}
                  </select>
                  <select
                    value={formData.issueMonth}
                    onChange={(e) =>
                      handleInputChange("issueMonth", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option>- Chọn tháng -</option>
                    {months.map((m) => (
                      <option key={`m-${m}`}>{m}</option>
                    ))}
                  </select>
                  <select
                    value={formData.issueYear}
                    onChange={(e) =>
                      handleInputChange("issueYear", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option>- Chọn năm cấp -</option>
                    {years.map((y) => (
                      <option key={`y1-${y}`}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Publication Date */}
              <div className=" rounded-md p-4 bg-gray-50">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Năm công bố bằng <span className="text-red-500">(*)</span>
                </label>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <select
                    value={formData.publicationDay}
                    onChange={(e) =>
                      handleInputChange("publicationDay", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option>- Chọn ngày -</option>
                    {days.map((d) => (
                      <option key={`pd-${d}`}>{d}</option>
                    ))}
                  </select>
                  <select
                    value={formData.publicationMonth}
                    onChange={(e) =>
                      handleInputChange("publicationMonth", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option>- Chọn tháng -</option>
                    {months.map((m) => (
                      <option key={`pm-${m}`}>{m}</option>
                    ))}
                  </select>
                  <select
                    value={formData.publicationYear}
                    onChange={(e) =>
                      handleInputChange("publicationYear", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option>- Chọn năm -</option>
                    {years.map((y) => (
                      <option key={`py-${y}`}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Expiration Date */}
              <div className=" rounded-md p-4 bg-gray-50">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Năm hết hạn <span className="text-red-500">(*)</span>
                </label>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <select
                    value={formData.expirationDay}
                    onChange={(e) =>
                      handleInputChange("expirationDay", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option>- Chọn ngày -</option>
                    {days.map((d) => (
                      <option key={`ed-${d}`}>{d}</option>
                    ))}
                  </select>
                  <select
                    value={formData.expirationMonth}
                    onChange={(e) =>
                      handleInputChange("expirationMonth", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option>- Chọn tháng -</option>
                    {months.map((m) => (
                      <option key={`em-${m}`}>{m}</option>
                    ))}
                  </select>
                  <select
                    value={formData.expirationYear}
                    onChange={(e) =>
                      handleInputChange("expirationYear", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option>- Chọn năm -</option>
                    {years.map((y) => (
                      <option key={`ey-${y}`}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            {/* Minh chứng kèm */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minh chứng kèm
              </label>
              <textarea
                value={formData.evidence}
                onChange={(e) => handleInputChange("evidence", e.target.value)}
                className="w-full h-32 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Nhập minh chứng kèm..."
              />
            </div>

            {/* Ghi chú */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ghi chú
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleInputChange("notes", e.target.value)}
                className="w-full h-24 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Nhập ghi chú..."
              />
            </div>

            {/* File Upload */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  File đính kèm (Bạn có thể chọn nhiều file một lần!)
                </label>
                <div className="flex items-center space-x-4">
                  <input
                    type="file"
                    multiple
                    onChange={(e) => handleFileSelect(e.target.files)}
                    className="hidden"
                    id="ip-file-upload"
                  />
                  <label
                    htmlFor="ip-file-upload"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 cursor-pointer transition-colors"
                  >
                    Choose Files
                  </label>
                  <span className="text-sm text-gray-500">
                    {formData.attachedFiles.length === 0
                      ? "No file chosen"
                      : `${formData.attachedFiles.length} file(s) chosen`}
                  </span>
                </div>

                {formData.attachedFiles.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Files:
                    </h4>
                    <div
                      className={`space-y-2 ${
                        formData.attachedFiles.length > 3
                          ? "max-h-48 overflow-y-auto pr-2"
                          : ""
                      }`}
                    >
                      {formData.attachedFiles.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center space-x-2 min-w-0 flex-1">
                            <svg
                              className="w-4 h-4 text-gray-400 flex-shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {file.name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {(file.size / 1024).toFixed(1)} KB
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              const updatedFiles =
                                formData.attachedFiles.filter(
                                  (_, i) => i !== index
                                );
                              handleInputChange("attachedFiles", updatedFiles);
                            }}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded transition-colors flex-shrink-0"
                            title="Xóa file"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <ProjectFormActions
          onSave={handleSave}
          onCancel={() => window.history.back()}
        />
      </div>
    </PageShell>
  );
};

export default AddNewIntellectualProperty;
